import { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  updateVideoProgress,
  getVideoProgress,
  resetVideoProgress,
  syncLocalToApi,
} from '../services/videoProgressHybridService';

const FLUSH_INTERVAL_MS = 5000;

/**
 * Hook to track video playback progress.
 *
 * - Starts tracking on the 'play' event (not on mount).
 * - Counts only forward playback time, ignoring seeks.
 * - Flushes to hybrid service (API-first, localStorage fallback) on pause/ended/interval.
 * - Increments viewCount once per page visit after threshold is met.
 * - Syncs localStorage → API once per session on mount.
 *
 * @param {string|null} userId - MSAL localAccountId
 * @param {string|null} videoId - Microconcepto ID
 * @param {React.RefObject} [externalVideoRef] - Optional external video ref
 * @param {*} [videoReadySignal] - Changes when video element becomes available in DOM
 * @returns {{ videoRef, watched, viewCount, secondsWatched, resetWatched }}
 */
export function useVideoProgress(userId, videoId, externalVideoRef = null, videoReadySignal = null) {
  const navigate = useNavigate();
  const internalVideoRef = useRef(null);
  const videoRef = externalVideoRef || internalVideoRef;
  const [watched, setWatched] = useState(false);
  const [viewCount, setViewCount] = useState(0);
  const [secondsWatched, setSecondsWatched] = useState(0);

  // Mutable refs for tracking state without re-renders
  const accumulatedRef = useRef(0);
  const lastTimeRef = useRef(null);
  const sessionCountedRef = useRef(false);
  const playStartedRef = useRef(false);
  const durationRef = useRef(null);
  const flushTimerRef = useRef(null);

  // Keep navigate in a ref so flush callback doesn't depend on it
  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  // Sync localStorage → API once per user session
  useEffect(() => {
    if (!userId) return;
    void syncLocalToApi(userId, navigateRef.current).catch(() => {});
  }, [userId]);

  // Load existing progress on mount (async)
  useEffect(() => {
    if (!userId || !videoId) return;
    let cancelled = false;

    (async () => {
      try {
        const existing = await getVideoProgress(userId, videoId, navigateRef.current);
        if (cancelled) return;
        if (existing) {
          setWatched(existing.watched);
          setViewCount(existing.viewCount);
          setSecondsWatched(existing.secondsWatched);
          accumulatedRef.current = existing.secondsWatched;
          sessionCountedRef.lastCount = existing.viewCount;
        } else {
          setWatched(false);
          setViewCount(0);
          setSecondsWatched(0);
          accumulatedRef.current = 0;
          sessionCountedRef.lastCount = 0;
        }
      } catch {
        // Fallback state already set to defaults
      }
    })();

    // Reset session state for new video
    sessionCountedRef.current = false;
    playStartedRef.current = false;
    lastTimeRef.current = null;

    return () => { cancelled = true; };
  }, [userId, videoId]);

  // Fire-and-forget async flush — safe to call from event handlers & intervals
  const flush = useCallback(() => {
    if (!userId || !videoId || !playStartedRef.current) return;

    void (async () => {
      try {
        const result = await updateVideoProgress(userId, videoId, {
          secondsWatched: accumulatedRef.current,
          videoDuration: durationRef.current,
          countNewView: !sessionCountedRef.current && accumulatedRef.current >= (
            durationRef.current
              ? Math.min(10, durationRef.current * 0.8)
              : 10
          ),
        }, navigateRef.current);

        if (result) {
          if (!sessionCountedRef.current && result.viewCount > sessionCountedRef.lastCount) {
            sessionCountedRef.current = true;
          }
          sessionCountedRef.lastCount = result.viewCount;
          setWatched(result.watched);
          setViewCount(result.viewCount);
          setSecondsWatched(result.secondsWatched);
        }
      } catch {
        // Flush failed — hybrid service already wrote to localStorage as backup
      }
    })();
  }, [userId, videoId]);

  // Attach/detach listeners when video element becomes available or deps change
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !userId || !videoId) return;

    const onLoadedMetadata = () => {
      durationRef.current = video.duration;
    };

    const onPlay = () => {
      playStartedRef.current = true;
      lastTimeRef.current = video.currentTime;
      // Start periodic flush
      if (!flushTimerRef.current) {
        flushTimerRef.current = setInterval(flush, FLUSH_INTERVAL_MS);
      }
    };

    const onTimeUpdate = () => {
      if (!playStartedRef.current || lastTimeRef.current === null) return;

      const current = video.currentTime;
      const delta = current - lastTimeRef.current;

      // Only count forward playback (0 < delta < 2s to ignore seeks)
      if (delta > 0 && delta < 2) {
        accumulatedRef.current += delta;
      }
      lastTimeRef.current = current;
    };

    const onPause = () => {
      flush();
    };

    const onEnded = () => {
      flush();
      if (flushTimerRef.current) {
        clearInterval(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };

    const onSeeked = () => {
      // Reset lastTime after seek to avoid counting the jump
      lastTimeRef.current = video.currentTime;
    };

    // Bind
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('play', onPlay);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('seeked', onSeeked);

    // If metadata already loaded
    if (video.duration) {
      durationRef.current = video.duration;
    }

    return () => {
      // Flush on cleanup
      flush();
      clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;

      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('seeked', onSeeked);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, videoId, flush, videoReadySignal]);

  // Flush on page hide (tab close, navigate away)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flush();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [flush]);

  const resetWatched = useCallback(() => {
    if (!userId || !videoId) return;
    void (async () => {
      try {
        const result = await resetVideoProgress(userId, videoId, navigateRef.current);
        if (result) {
          setWatched(false);
          setViewCount(0);
          setSecondsWatched(0);
          accumulatedRef.current = 0;
          sessionCountedRef.current = false;
          sessionCountedRef.lastCount = 0;
        }
      } catch {
        // Reset failed — will retry next time
      }
    })();
  }, [userId, videoId]);

  return { videoRef, watched, viewCount, secondsWatched, resetWatched };
}
